/**
 * Solar Hijri month grid for calendar popovers (material-standard §3.5): Saturday-first weeks,
 * Friday marked, disabled cells outside optional min/max `LocalDate` bounds.
 */

import { CalendarDate, PersianCalendar, toCalendar } from '@internationalized/date';

import {
  type LocalDate,
  parseLocalDate,
  solarHijriToLocalDate,
  type SolarHijriDate,
  weekdayOf,
} from './calendar.js';

const persian = new PersianCalendar();

export interface SolarMonthGridCell {
  readonly disabled: boolean;
  readonly inMonth: boolean;
  readonly isFriday: boolean;
  readonly localDate: LocalDate;
  readonly solar: SolarHijriDate;
}

export interface SolarMonthGridModel {
  readonly month: number;
  readonly weeks: readonly (readonly SolarMonthGridCell[])[];
  readonly year: number;
}

function solarFromLocal(value: LocalDate): SolarHijriDate {
  const converted = toCalendar(parseLocalDate(value), persian);
  return { day: converted.day, month: converted.month, year: converted.year };
}

function compareLocalDate(left: LocalDate, right: LocalDate): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function isDisabled(
  localDate: LocalDate,
  min: LocalDate | undefined,
  max: LocalDate | undefined,
): boolean {
  if (min !== undefined && compareLocalDate(localDate, min) < 0) {
    return true;
  }
  if (max !== undefined && compareLocalDate(localDate, max) > 0) {
    return true;
  }
  return false;
}

/** Builds a six-row Saturday-first grid for the given Solar month (may include leading/trailing days). */
export function buildSolarMonthGrid({
  max,
  min,
  month,
  year,
}: {
  readonly max?: LocalDate;
  readonly min?: LocalDate;
  readonly month: number;
  readonly year: number;
}): SolarMonthGridModel {
  if (month < 1 || month > 12) {
    throw new RangeError(`Not a valid Solar Hijri month: ${month}`);
  }

  const monthStart = solarHijriToLocalDate({ day: 1, month, year });
  const daysInMonth = persian.getDaysInMonth(new CalendarDate(persian, year, month, 1));
  const monthEnd = solarHijriToLocalDate({ day: daysInMonth, month, year });

  const startDay = parseLocalDate(monthStart);
  const startWeekday = new Date(
    Date.UTC(startDay.year, startDay.month - 1, startDay.day),
  ).getUTCDay();
  // Saturday = column 0; JS Sunday = 0 … Saturday = 6 → offset from previous Saturday.
  const leading = (startWeekday + 1) % 7;
  const gridStart = startDay.subtract({ days: leading });

  const weeks: SolarMonthGridCell[][] = [];
  let cursor = gridStart;
  for (let week = 0; week < 6; week += 1) {
    const row: SolarMonthGridCell[] = [];
    for (let column = 0; column < 7; column += 1) {
      const localDate = `${String(cursor.year).padStart(4, '0')}-${String(cursor.month).padStart(2, '0')}-${String(cursor.day).padStart(2, '0')}`;
      const solar = solarFromLocal(localDate);
      const inMonth =
        compareLocalDate(localDate, monthStart) >= 0 && compareLocalDate(localDate, monthEnd) <= 0;
      row.push({
        disabled: isDisabled(localDate, min, max),
        inMonth,
        isFriday: weekdayOf(localDate) === 'جمعه',
        localDate,
        solar,
      });
      cursor = cursor.add({ days: 1 });
    }
    weeks.push(row);
  }

  return { month, weeks, year };
}
